import { EditOutlined, Key } from '@mui/icons-material'
import PermissionsContent from './PermissionsContent'
import React, { FC, Fragment, ReactNode, useState } from 'react'
import ManagingSets from './ManagingSets'
import { mergeArrays } from '../../../../lib/utils/helpers'
import { Dialog, IconButton } from '../../../ui'
import cn from '../../../../lib/utils/cn'
import { useGetObjectPermissions } from '../../../../hooks/api/permissionHooks'
import { DocumentData } from '../../../../lib/api/types/document.interfaces'
import FileFormatIcon from '../../../common/FileFormatIcon'
import { useAuth } from '../../../../hooks'

interface ConfigPermissionsModalProps {
	isOpen: boolean
	onClose: () => void
	currentObject: DocumentData | null
}

const ConfigPermissionsModal: FC<ConfigPermissionsModalProps> = ({
	isOpen,
	onClose,
	currentObject,
}) => {
	const { isAuthenticated, currentUser, isAdmin } = useAuth()
	const [isSetsManaging, setIsSetsManaging] = useState(false)

	const {
		data: currentObjectData,
		isLoading: isLoadingPermissions,
		isRefetching: isRefetchingPermissions,
		refetch,
	} = useGetObjectPermissions(
		{
			id: currentObject?.id ?? '',
		},
		{ enabled: Boolean(currentObject?.id && isAuthenticated && isOpen) }
	)

	const handleClose = () => {
		setIsSetsManaging(false)
		onClose()
	}

	const isLoading = isLoadingPermissions || isRefetchingPermissions

	if (!isOpen || !currentObject) {
		return null
	}

	return (
		<Dialog
			isOpen={isOpen}
			onClose={handleClose}
			icon={<Key />}
			title={isSetsManaging ? 'Permissions' : 'Manage Access'}
			className="w-[520px] overflow-visible"
		>
			{isSetsManaging ? (
				<ManagingSets
					isAdmin={isAdmin}
					isVisible={isSetsManaging}
					organizationId={currentUser?.organization?.id ?? ''}
					currentObject={currentObject}
					currentSet={currentObjectData?.data?.permissionSet}
					onCancel={() => setIsSetsManaging(false)}
					onRefetch={refetch}
				/>
			) : (
				<Fragment>
					<p className="font-bold text-lg mb-3 flex items-center justify-start gap-1 text-text-main dark:text-text-main-dark">
						<FileFormatIcon
							extension={currentObject?.fileType}
							fileName={currentObject?.name}
						/>
						{currentObject?.name}
					</p>
					<p className="flex items-center gap-2 mb-6 h-6">
						<span className="text-text-table dark:text-text-table-dark text-sm font-normal">
							Your Permission:
						</span>
						<LoadingWrapper loading={isLoading}>
							<Key className="fill-stroke-dark w-4 h-4" />
							<span className="text-text-main dark:text-text-main-dark text-base font-semibold">
								{currentObjectData?.data?.currentUser === 1
									? 'Write'
									: currentObjectData?.data?.currentUser === 2
										? 'Delete'
										: currentObjectData?.data?.currentUser === 3
											? 'None'
											: 'Read'}
							</span>
						</LoadingWrapper>
					</p>
					<div className="flex items-center justify-start mb-4 h-6">
						<span className="text-text-table dark:text-text-table-dark text-sm font-normal text-nowrap">
							Permission set:
						</span>

						<LoadingWrapper loading={isLoading}>
							<span
								className={cn(
									'ellipsis text-base mr-2.5 ml-1.5',
									currentObjectData?.data?.permissionSet
										? 'text-text-main dark:text-text-main-dark font-semibold'
										: 'text-text-secondary dark:text-text-table-dark font-normal'
								)}
							>
								{currentObjectData?.data?.permissionSet?.name ??
									'No selected set'}
							</span>

							{(isAdmin ||
								currentUser?.id === currentObject?.createdBy?.id) && (
									<IconButton
										variant="secondary"
										className="w-7 h-7"
										icon={<EditOutlined className="fill-inherit h-5" />}
										onClick={() => setIsSetsManaging(true)}
										tooltip={
											currentObjectData?.data?.permissionSet
												? 'Edit set'
												: 'Select set'
										}
									/>
								)}
						</LoadingWrapper>
					</div>
					<PermissionsContent
						currentPermissions={mergeArrays(
							currentObjectData?.data?.permissionSet?.permissions,
							currentObjectData?.data?.computed
						)?.map(({ level, type, to: { id, name, email } }) => ({
							level,
							type,
							id,
							name: name || email,
						}))}
						currentSet={currentObjectData?.data?.permissionSet}
						currentUser={currentUser}
						currentObject={currentObject}
						isLoading={isLoading}
						isEditable={
							isAdmin || currentUser?.id === currentObject?.createdBy?.id
						}
						isAdmin={isAdmin}
						onClose={handleClose}
					/>
				</Fragment>
			)}
		</Dialog>
	)
}

export default ConfigPermissionsModal

const LoadingWrapper = ({
	loading,
	children,
}: {
	loading: boolean
	children: ReactNode
}) =>
	loading ? (
		<span className="text-text-secondary ml-1.5 text-base">Loading...</span>
	) : (
		<Fragment>{children}</Fragment>
	)
